import type { ToolId } from '../layout/workspace-config.ts';
import type { ReportId } from '../reports/report-registry.ts';

export const PROJECT_DATA_LISTENER_IDS = [
    'schedule',
    'attendance',
    'blocking',
    'feed',
    'costumes',
    'actionItems',
    'transcription',
] as const;

export type ProjectListenerId = typeof PROJECT_DATA_LISTENER_IDS[number];

export const PROJECT_DATA_RESOURCE_IDS = [
    'projectMeta',
    'showStructure',
    'runSheet',
    'scriptBlocks',
    'personnel',
    'rawNotes',
    'props',
    'sets',
    'sound',
    'lighting',
    'promptBook',
    'fly',
    'projections',
    'schedule',
    'attendance',
    'blocking',
    'feed',
    'costumes',
    'actionItems',
    'transcription',
    'files',
    'activityLog',
] as const;

export type ProjectDataResourceId = typeof PROJECT_DATA_RESOURCE_IDS[number];

export const GLOBAL_PROJECT_DATA_RESOURCES = ['projectMeta', 'showStructure', 'personnel'] as const satisfies readonly ProjectDataResourceId[];

// fallow-ignore-next-line unused-export
export const TAB_SCOPED_PROJECT_DATA_RESOURCES = [
    'props',
    'sets',
    'sound',
    'lighting',
    'promptBook',
    'fly',
    'projections',
] as const satisfies readonly ProjectDataResourceId[];

export const CLOUD_BASELINE_PROJECT_DATA_RESOURCES = [
    ...GLOBAL_PROJECT_DATA_RESOURCES,
    ...TAB_SCOPED_PROJECT_DATA_RESOURCES,
] as const satisfies readonly ProjectDataResourceId[];

export type ProjectSurfaceId = 'layoutSidebar';

export interface ProjectDataRequirementInput {
    toolIds?: readonly (string | null | undefined)[];
    reportIds?: readonly (string | null | undefined)[];
    surfaces?: readonly ProjectSurfaceId[];
}

type ProjectDataRequirementSource = ProjectDataRequirementInput | string | null | undefined;

const PROJECT_DATA_CLIENT_RESOURCE_LOOKUP: ReadonlySet<ProjectDataResourceId> = new Set(
    CLOUD_BASELINE_PROJECT_DATA_RESOURCES,
);

const BASELINE_PROJECT_RESOURCES: readonly ProjectDataResourceId[] = GLOBAL_PROJECT_DATA_RESOURCES;

export const PROJECT_DATA_RESOURCE_REQUIREMENTS_BY_TOOL = {
    dashboard: ['schedule', 'attendance', 'feed', 'scriptBlocks', 'props'],
    'show-structure': ['props', 'sound'],
    'rehearsal-script': ['scriptBlocks'],
    personnel: [],
    scheduler: ['schedule'],
    calendar: ['schedule'],
    notes: ['rawNotes', 'schedule', 'attendance', 'files', 'transcription'],
    'action-items': ['actionItems', 'rawNotes', 'schedule', 'attendance', 'personnel', 'transcription'],
    'set-builder': ['sets'],
    blocking: ['sets', 'blocking', 'scriptBlocks'],
    'blocking-snapshot': ['sets', 'blocking', 'scriptBlocks'],
    props: ['props'],
    costumes: ['costumes'],
    sound: ['sound'],
    video: ['transcription'],
    email: ['schedule'],
    feed: ['feed', 'schedule', 'props'],
    files: ['files'],
    scenes: ['runSheet', 'fly'],
    budget: ['props', 'costumes'],
    production: ['schedule', 'props', 'costumes'],
    settings: [],
    lighting: ['lighting'],
    'prompt-book': ['promptBook', 'scriptBlocks', 'sound', 'lighting', 'fly'],
    'line-notes': ['scriptBlocks', 'schedule', 'attendance'],
    performance: ['promptBook', 'scriptBlocks', 'sound', 'lighting', 'fly', 'projections'],
    projections: ['projections'],
    'activity-log': ['activityLog'],
    dev: [],
} satisfies Readonly<Record<ToolId, readonly ProjectDataResourceId[]>>;

export const PROJECT_DATA_RESOURCE_REQUIREMENTS_BY_REPORT = {
    scenes: ['runSheet', 'props'],
    props: ['props'],
    cast: [],
    conflicts: [],
    breakdown: [],
    schedule: ['schedule'],
    callsheet: ['schedule'],
    signin: ['schedule'],
    blocking: ['sets', 'blocking'],
    costumebible: ['costumes'],
    quickchange: ['costumes'],
    deadlines: ['schedule'],
    micplot: ['sound'],
    'sound-cues': ['sound'],
    lxcuelist: ['lighting'],
    followspot: ['lighting'],
    callingscript: ['promptBook', 'scriptBlocks'],
    cuesynopsis: ['promptBook', 'scriptBlocks', 'sound', 'lighting'],
    'actor-rehearsal-hours': ['schedule', 'attendance'],
} satisfies Readonly<Record<ReportId, readonly ProjectDataResourceId[]>>;

const RESOURCE_REQUIREMENTS_BY_SURFACE: Readonly<Record<ProjectSurfaceId, readonly ProjectDataResourceId[]>> = {
    layoutSidebar: ['feed'],
};

const FIREBASE_LISTENERS_BY_RESOURCE: Partial<Record<ProjectDataResourceId, ProjectListenerId>> = {
    schedule: 'schedule',
    attendance: 'attendance',
    blocking: 'blocking',
    feed: 'feed',
    costumes: 'costumes',
    actionItems: 'actionItems',
    transcription: 'transcription',
};

const CENTRAL_FIREBASE_LISTENER_REQUIREMENTS_BY_TOOL: Readonly<Record<string, readonly ProjectListenerId[]>> = {
    dashboard: ['schedule', 'attendance', 'feed'],
    email: ['schedule'],
    feed: ['schedule'],
    notes: ['schedule', 'attendance'],
    'action-items': ['actionItems'],
    'line-notes': ['schedule', 'attendance'],
    production: ['schedule'],
};

function normalizeRequirementInput(source: ProjectDataRequirementSource): ProjectDataRequirementInput {
    if (!source) {
        return {};
    }

    if (typeof source === 'string') {
        return { toolIds: [source] };
    }

    return source;
}

function addResources(
    resources: ProjectDataResourceId[],
    seen: Set<ProjectDataResourceId>,
    nextResources: readonly ProjectDataResourceId[] | undefined
): void {
    if (!nextResources) {
        return;
    }

    for (const resource of nextResources) {
        if (!seen.has(resource)) {
            seen.add(resource);
            resources.push(resource);
        }
    }
}

export function deriveProjectDataResources(source: ProjectDataRequirementSource): ProjectDataResourceId[] {
    const input = normalizeRequirementInput(source);
    const resources: ProjectDataResourceId[] = [];
    const seen = new Set<ProjectDataResourceId>();

    addResources(resources, seen, BASELINE_PROJECT_RESOURCES);

    for (const toolId of input.toolIds ?? []) {
        if (toolId) {
            addResources(
                resources,
                seen,
                PROJECT_DATA_RESOURCE_REQUIREMENTS_BY_TOOL[toolId as ToolId]
            );
        }
    }

    for (const reportId of input.reportIds ?? []) {
        if (reportId) {
            addResources(
                resources,
                seen,
                PROJECT_DATA_RESOURCE_REQUIREMENTS_BY_REPORT[reportId as ReportId]
            );
        }
    }

    for (const surface of input.surfaces ?? []) {
        addResources(resources, seen, RESOURCE_REQUIREMENTS_BY_SURFACE[surface]);
    }

    return resources;
}

export function deriveNeededProjectDataResources(source: ProjectDataRequirementSource): ProjectDataResourceId[] {
    const resources: ProjectDataResourceId[] = [];
    const seen = new Set<ProjectDataResourceId>();

    for (const resource of deriveProjectDataResources(source)) {
        if (PROJECT_DATA_CLIENT_RESOURCE_LOOKUP.has(resource) && !seen.has(resource)) {
            seen.add(resource);
            resources.push(resource);
        }
    }

    return resources;
}

export function getFirebaseProjectListenersForRequirements(
    source: ProjectDataRequirementSource,
): ProjectListenerId[] {
    const listeners: ProjectListenerId[] = [];
    const seen = new Set<ProjectListenerId>();

    for (const resource of deriveProjectDataResources(source)) {
        const listener = FIREBASE_LISTENERS_BY_RESOURCE[resource];
        if (listener && !seen.has(listener)) {
            seen.add(listener);
            listeners.push(listener);
        }
    }

    return listeners;
}

function getCentralProjectListenersForTool(
    activeToolId: string | null | undefined,
): readonly ProjectListenerId[] {
    if (!activeToolId) {
        return [];
    }

    return CENTRAL_FIREBASE_LISTENER_REQUIREMENTS_BY_TOOL[activeToolId] ?? [];
}

export function toolNeedsCentralProjectListener(
    activeToolId: string | null | undefined,
    listenerId: ProjectListenerId,
): boolean {
    return getCentralProjectListenersForTool(activeToolId).includes(listenerId);
}
